/**
 * Minimized Loading Box Component
 *
 * Small floating box that appears below the navbar while insurance is being verified.
 * Positioning is handled by the parent wrapper — this component only renders the content.
 */

"use client";

import {
  AnimatePresence,
  animate,
  motion,
  useMotionValue,
  useTransform,
} from "framer-motion";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useInsuranceConnectorContext } from "../insurance-connector-provider";
import { InsuranceLogo } from "@frontend/components/InsuranceStepper/InsuranceLogo";

const ANIMATION_DURATION = 15000;

const PHRASE_INTERVAL = 3000;

interface MinimizedLoadingBoxProps {
  isMobile?: boolean;
}

export const MinimizedLoadingBox = ({
  isMobile = false,
}: MinimizedLoadingBoxProps) => {
  const t = useTranslations("InsuranceConnector");
  const loadingPhrases = [
    t("EstablishingConnection"),
    t("FetchingBenefits"),
    t("CreatingPrices"),
  ];
  const context = useInsuranceConnectorContext();
  const [percentage, setPercentage] = useState(0);
  const [xDistance, setXDistance] = useState(0);
  const movingRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  // Mask edge tracks the icon's right edge with an 8px soft falloff, so dots
  // appear to dissolve behind the superscript as it slides right.
  const maskImage = useTransform(
    x,
    (v) => `linear-gradient(to right, transparent ${v}px, black ${v + 8}px)`,
  );

  const [phraseIndex, setPhraseIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setPhraseIndex((prev) => (prev + 1) % loadingPhrases.length);
    }, PHRASE_INTERVAL);
    return () => clearInterval(interval);
  }, [loadingPhrases.length]);

  const primaryPayer = context.primaryInsurance?.payer;
  const secondaryPayer = context.secondaryInsurance?.payer;
  const hasSecondary = !!secondaryPayer;

  // Measure icon distance after first paint
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (movingRef.current && targetRef.current) {
        const start = movingRef.current.getBoundingClientRect();
        const end = targetRef.current.getBoundingClientRect();
        setXDistance(end.left - start.left);
      }
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  // Animate percentage from 0 → 95
  useEffect(() => {
    const interval = setInterval(() => {
      setPercentage((prev) => {
        if (prev >= 95) return prev;
        return Math.min(prev + 1, 95);
      });
    }, ANIMATION_DURATION / 95);

    return () => clearInterval(interval);
  }, []);

  // Animate the moving icon toward the target. The same motion value also
  // drives the dot-row mask below, so the two stay perfectly in sync.
  useEffect(() => {
    if (xDistance > 0) {
      const controls = animate(x, xDistance - 80, {
        duration: ANIMATION_DURATION / 1000,
        ease: "linear",
      });
      return controls.stop;
    }
  }, [xDistance, x]);

  return (
    <div className={`rounded-3xl ${isMobile ? "w-full p-3" : "w-96 p-4"}`}>
      <div className="flex flex-col gap-6">
        <div className="w-full flex items-center gap-3">
          <div className="text-2xl font-bold tabular-nums min-w-[60px]">
            {percentage}%
          </div>

          <div className="flex-1 relative h-8">
            <motion.div ref={movingRef} className="absolute z-10" style={{ x }}>
              <Image
                src="/icons/SuperscriptCurvesIcon.svg"
                alt=""
                width={38}
                height={38}
              />
            </motion.div>

            <motion.div
              aria-hidden
              className="absolute inset-y-0 left-[38px] right-[44px] my-auto h-[3px] bg-[radial-gradient(circle,theme(colors.gray.300)_1.5px,transparent_1.5px)] bg-[length:8px_3px] bg-repeat-x"
              style={{ WebkitMaskImage: maskImage, maskImage }}
            />

            <div
              ref={targetRef}
              className="absolute right-0 flex items-center justify-end"
            >
              {primaryPayer && (
                <div className="relative flex items-center justify-center">
                  <div className="relative bg-white w-[40px] h-[40px] flex rounded-full items-center justify-center z-10">
                    <InsuranceLogo
                      className="w-[40px] h-[40px]"
                      insuranceName={primaryPayer}
                    />
                  </div>
                  {hasSecondary && secondaryPayer && (
                    <div
                      className="absolute bg-white w-[40px] h-[40px] flex rounded-full items-center justify-center z-0 opacity-90"
                      style={{ right: "70%", transform: "translateX(50%)" }}
                    >
                      <InsuranceLogo
                        className="w-[40px] h-[40px]"
                        insuranceName={secondaryPayer}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="relative h-14">
          <AnimatePresence mode="wait">
            <motion.p
              key={phraseIndex}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-x-0 top-0 text-[20px] leading-[120%] font-medium text-black"
            >
              {loadingPhrases[phraseIndex]}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
